import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { serviceLd, faqLd, breadcrumbLd } from "@/lib/seo";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { SetPasswordCard } from "@/components/SetPasswordCard";
import {
  getService,
  getSiteCopy,
  getConfig,
  createCheckoutSession,
  getCheckoutStatus,
} from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { Check, Calendar, FileText } from "lucide-react";
import { amp } from "@/lib/amp";

const FOUNDER_SLUG = "founder-tech-clarity-sprint";
const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};
const MAX_POLLS = 8;

const labelClass = "text-[11px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]";

export default function Sprint() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { sprintSlug } = useParams();
  const SLUG = sprintSlug || FOUNDER_SLUG;
  const isFounderSprint = SLUG === FOUNDER_SLUG;
  const sessionId = params.get("session_id");

  const { data: service } = useQuery({ queryKey: ["service", SLUG], queryFn: () => getService(SLUG) });
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });
  const { data: config = {} } = useQuery({ queryKey: ["config"], queryFn: getConfig });

  // checkout form
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  const [phase, setPhase] = useState(sessionId ? "polling" : "idle"); // idle|creating|polling|success|error|cancelled
  const [error, setError] = useState("");
  const [activation, setActivation] = useState(null);
  const paidFired = useRef(false);

  const arc = [1, 2, 3, 4].map((i) => ({
    title: copy[`sprint_arc_${i}_title`],
    body: copy[`sprint_arc_${i}_body`],
  }));
  const walkaway = [1, 2, 3, 4].map((i) => copy[`sprint_walk_${i}`]).filter(Boolean);
  const faq = [1, 2, 3, 4]
    .map((i) => ({ q: copy[`sprint_faq_${i}_q`], a: copy[`sprint_faq_${i}_a`] }))
    .filter((f) => f.q);

  // ---- polling ----
  const poll = useCallback(
    async (attempt = 0) => {
      if (attempt >= MAX_POLLS) {
        setPhase("error");
        setError("We could not confirm the payment in time. If you were charged, check your email or contact ceo@coppervine.io.");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          if (!paidFired.current) {
            paidFired.current = true;
            track("payment", {
              offer: data.offer_name,
              value: Number(String(service?.price_label || "").replace(/[^0-9.]/g, "")) || 2500,
              currency: (data.currency || "usd").toUpperCase(),
              transaction_id: sessionId,
            });
          }
          setActivation(data);
          setPhase("success");
          return;
        }
        if (data.status === "expired") {
          setPhase("error");
          setError("This checkout session expired. Please start again.");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId, service]
  );

  useEffect(() => {
    if (sessionId) {
      setPhase("polling");
      poll(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startCheckout = async (e) => {
    e.preventDefault();
    setError("");
    if (!buyer.name.trim() || !buyer.email.trim()) {
      setError("Name and email are required to start.");
      return;
    }
    setPhase("creating");
    try {
      const data = await createCheckoutSession({
        product_key: service?.product_key || FOUNDER_SLUG,
        origin_url: window.location.origin,
        name: buyer.name,
        email: buyer.email,
        sprint_slug: SLUG,
        utm: getUTM(),
      });
      track("door_click", { target: "stripe_checkout", offer: SLUG });
      window.location.href = data.url;
    } catch (err) {
      setPhase("idle");
      const detail = err?.response?.data?.detail;
      setError(detail || "Could not start checkout. Please try again.");
    }
  };

  const bookReadout = () => {
    track("booking_start", { offer: SLUG });
    if (config.motion_sprint_url) {
      window.open(config.motion_sprint_url, "_blank", "noopener");
    }
  };

  // ===================== SUCCESS / POLLING STATES =====================
  if (sessionId) {
    return (
      <div className="exec-scope min-h-screen" data-testid="sprint-purchase-page">
        <div className="exec-container pt-24 md:pt-28 pb-24 max-w-2xl">
          {phase === "polling" && (
            <div data-testid="sprint-purchase-processing">
              <p className="exec-eyebrow">Confirming</p>
              <h1 className="font-light text-[clamp(26px,3.2vw,38px)] leading-[1.14] mt-4">Confirming your payment</h1>
              <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-4">
                One moment. We are confirming the charge and starting your
                activation. Do not close this tab.
              </p>
              <div className="mt-8 space-y-3">
                <Skeleton className="h-12 w-full bg-[color:var(--rule)]" />
                <Skeleton className="h-12 w-3/4 bg-[color:var(--rule)]" />
              </div>
            </div>
          )}

          {phase === "success" && (
            <div data-testid="sprint-purchase-success-state">
              <div className="w-12 h-12 rounded-full border border-[color:var(--copper)] flex items-center justify-center">
                <Check size={22} className="text-[color:var(--copper-deep)]" />
              </div>
              <p className="exec-eyebrow mt-6">Payment confirmed</p>
              <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
                The Sprint is yours. Two steps left.
              </h1>
              <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
                A welcome email is on the way to{" "}
                <span className="text-[color:var(--ink)]">{activation?.buyer_email}</span>{" "}
                with your booking link. You can also continue right here.
              </p>

              <div className="mt-8 space-y-4">
                <SetPasswordCard email={activation?.buyer_email} sessionId={sessionId} />
                <div className="exec-card p-5 flex items-start gap-4">
                  <FileText size={20} className="text-[color:var(--copper-deep)] mt-1 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">1. Complete your intake</h3>
                    <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-2">
                      Your stack, your stage, the gap. Five minutes. It scopes the Sprint.
                    </p>
                    <button
                      data-testid="sprint-go-intake-button"
                      className="exec-btn exec-btn-secondary mt-4"
                      onClick={() =>
                        navigate(
                          `/intake/${SLUG}?email=${encodeURIComponent(
                            activation?.buyer_email || ""
                          )}&session_id=${sessionId}`
                        )
                      }
                    >
                      Start intake
                    </button>
                  </div>
                </div>

                <div className="exec-card p-5 flex items-start gap-4">
                  <Calendar size={20} className="text-[color:var(--copper-deep)] mt-1 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">2. Book the readout</h3>
                    <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-2">
                      Pick your kickoff and readout time. One recorded session, yours to keep.
                    </p>
                    <button
                      data-testid="sprint-book-readout-button"
                      className="exec-btn exec-btn-primary mt-4"
                      onClick={bookReadout}
                    >
                      Book the readout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(phase === "error") && (
            <div
              data-testid="sprint-purchase-error-alert"
              className="border-l-2 border-[color:var(--copper)] pl-5 py-2"
            >
              <p className="exec-eyebrow">Something went wrong</p>
              <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-3">{error}</p>
              <button
                className="exec-btn exec-btn-secondary mt-6"
                onClick={() => {
                  setParams({});
                  setPhase("idle");
                  paidFired.current = false;
                }}
              >
                Back to the Sprint
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===================== IDLE / PURCHASE STATE =====================
  return (
    <div className="exec-scope min-h-screen" data-testid="sprint-purchase-page">
      {service && (
        <Seo
          title={`${service.name} — Copper & Vine`}
          description={(service.summary || service.description || `${service.name}. A Copper & Vine engagement.`).slice(0, 155)}
          path={`/services/${SLUG}`}
          jsonLd={[
            serviceLd({ name: service.name, description: service.summary || service.description || "", path: `/services/${SLUG}` }),
            ...(faq.length ? [faqLd(faq.map((f) => ({ q: f.q, a: f.a })))] : []),
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: service.name, path: `/services/${SLUG}` },
            ]),
          ]}
        />
      )}
      {/* Hero */}
      <section className="exec-container pt-24 md:pt-28 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="exec-eyebrow">Self-serve. Start today.</p>
            <h1 className="font-extralight text-[clamp(32px,4.6vw,58px)] leading-[1.1] mt-6">
              {amp(service?.name || "Founder Tech Clarity Sprint")}
            </h1>
            <p className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6 max-w-xl">
              {isFounderSprint ? copy.sprint_what_it_is : service?.description}
            </p>
          </div>

          {/* Purchase card */}
          <div className="lg:col-span-5">
            <motion.div {...fade} className="exec-card p-7 sm:p-8">
              <div className="flex items-baseline justify-between">
                <span className="exec-eyebrow">Fixed price</span>
                <span className="font-extralight text-[34px] leading-none">{service?.price_label || "$2,500"}</span>
              </div>
              <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-3">
                {service?.duration_label || "Four weeks"}. One recorded readout. No sales call required.
              </p>
              <div className="exec-rule my-6" />
              <form onSubmit={startCheckout} className="space-y-4">
                <label className="block">
                  <span className={labelClass}>Name</span>
                  <input
                    data-testid="sprint-buyer-name-input"
                    type="text"
                    value={buyer.name}
                    onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                    className="exec-underline"
                    autoComplete="name"
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Email</span>
                  <input
                    data-testid="sprint-buyer-email-input"
                    type="email"
                    value={buyer.email}
                    onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                    className="exec-underline"
                    autoComplete="email"
                  />
                </label>
                {error && (
                  <p
                    data-testid="sprint-purchase-error"
                    className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3"
                  >
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="sprint-purchase-submit-button"
                  disabled={phase === "creating"}
                  className="exec-btn exec-btn-primary w-full disabled:opacity-60"
                >
                  {phase === "creating" ? "Opening checkout\u2026" : "Buy now"}
                </button>
                <p className="exec-serif text-[14px] text-[color:var(--grey)] text-center">
                  Secure checkout by Stripe. Your email is verified before payment.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Four-week arc */}
      <section className="exec-container py-12">
        <p className="exec-eyebrow">The four-week arc</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-8 bg-[color:var(--rule)] p-px">
          {arc.map((a, i) => (
            <motion.div key={i} {...fade} className="bg-[color:var(--paper)] p-7">
              <p className="exec-serif italic text-[30px] text-[color:var(--copper)] leading-none">{String(i + 1).padStart(2, "0")}</p>
              <div className="exec-rule my-5" />
              <h3 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">{a.title}</h3>
              <p className="exec-serif text-[15.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">{a.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Deliverable + walk away (was sage, now light) */}
      <section className="exec-container py-12">
        <div className="exec-rule mb-14" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="exec-eyebrow">The deliverable</p>
            <p className="exec-serif text-[clamp(22px,2.8vw,32px)] leading-[1.3] text-[color:var(--ink)] mt-6">{copy.sprint_deliverable}</p>
          </div>
          <div>
            <p className="exec-eyebrow">What you walk away with</p>
            <ul className="mt-6 space-y-4">
              {walkaway.map((w, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="text-[color:var(--copper-deep)] mt-1 shrink-0" />
                  <span className="exec-serif text-[16.5px] leading-[1.5] text-[color:var(--lead-ink)]">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Proof point */}
      <section className="exec-container py-12">
        <div className="max-w-3xl">
          <p className="exec-eyebrow">One proof point</p>
          <p className="exec-serif text-[clamp(20px,2.4vw,28px)] leading-[1.3] text-[color:var(--ink)] mt-6">{copy.sprint_proof}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="exec-container pb-24">
        <div className="max-w-3xl">
          <p className="exec-eyebrow">Questions</p>
          <Accordion type="single" collapsible className="mt-6">
            {faq.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-[color:var(--rule)]"
              >
                <AccordionTrigger className="text-[15px] sm:text-[16px] font-normal text-[color:var(--ink)] text-left hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="exec-serif text-[16px] leading-[1.55] text-[color:var(--lead-ink)]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
